import { useEffect, useState } from "react";
import {
  WEEKDAY_LABELS,
  holidaysForYear,
  type AgendaRules,
  type Closure,
  type Weekday,
} from "@shared/modules/x-site";
import { Button } from "../../../core/components/ui/button";
import { Icon } from "../../../core/components/ui/icon";
import { Field, Input } from "../../../core/components/ui/input";
import { Modal } from "../../../core/components/ui/modal";
import { Toggle } from "../../../core/components/ui/toggle";
import { useToastStore } from "../../../core/stores/toast";
import { updateAgendaRules } from "../lib/api";
import { dayKey } from "../lib/dates";

// Les temps morts de l'atelier, gérés depuis l'agenda : une tempête de neige
// demain, un déneigement sur trois jours, une plage bloquée chaque semaine
// (et déplacée du vendredi au lundi), un férié finalement travaillé. Ce sont
// les réglages du site : le site et Léo n'offrent plus ces moments, et planifier
// dessus au CRM demande la dérogation habituelle.

const WEEKDAYS: Weekday[] = ["1", "2", "3", "4", "5", "6", "7"];
const today = () => dayKey(new Date());
const newId = () => `f${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;
const fullDate = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("fr-CA", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
const shortDate = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("fr-CA", { day: "numeric", month: "long" });
const hour = (hhmm: string) => {
  const [h, m] = hhmm.split(":");
  return m && m !== "00" ? `${Number(h)} h ${m}` : `${Number(h)} h`;
};

/** « Chaque vendredi dès 12 h, depuis le 9 octobre, sans fin » / « Du lundi 12 au mardi 13 janvier, toute la journée ». */
export function describeClosure(c: Closure): string {
  const when =
    c.startTime && c.endTime
      ? c.endTime >= "23:30"
        ? `dès ${hour(c.startTime)}`
        : `de ${hour(c.startTime)} à ${hour(c.endTime)}`
      : "toute la journée";
  if (c.weekdays && c.weekdays.length) {
    const days = c.weekdays.map((d) => WEEKDAY_LABELS[d].toLowerCase()).join(", ");
    const period = c.to
      ? `du ${shortDate(c.from)} au ${shortDate(c.to)}`
      : `depuis le ${shortDate(c.from)}, sans fin`;
    return `Chaque ${days} ${when}, ${period}`;
  }
  if (!c.to) return `Chaque jour ${when} depuis le ${shortDate(c.from)}, sans fin`;
  if (c.to === c.from) return `${fullDate(c.from)}, ${when}`;
  return `Du ${fullDate(c.from)} au ${fullDate(c.to)}, ${when}`;
}

interface OneOffDraft {
  from: string;
  to: string;
  allDay: boolean;
  startTime: string;
  endTime: string;
  label: string;
}
interface WeeklyDraft {
  weekdays: Weekday[];
  startTime: string;
  endTime: string;
  from: string;
  to: string;
  label: string;
}

function WeekdayChips({
  value,
  onChange,
}: {
  value: Weekday[];
  onChange: (next: Weekday[]) => void;
}) {
  return (
    <div className="closures__weekdays" role="group" aria-label="Jours de la semaine">
      {WEEKDAYS.map((d) => {
        const on = value.includes(d);
        return (
          <button
            key={d}
            type="button"
            className={`chips__chip${on ? " is-active" : ""}`}
            aria-pressed={on}
            title={WEEKDAY_LABELS[d]}
            onClick={() => onChange(on ? value.filter((x) => x !== d) : [...value, d].sort())}
          >
            {WEEKDAY_LABELS[d].slice(0, 3)}
          </button>
        );
      })}
    </div>
  );
}

interface ClosuresManagerProps {
  open: boolean;
  rules: AgendaRules | null;
  onClose: () => void;
  onSaved: (rules: AgendaRules) => void;
}

export function ClosuresManager({ open, rules, onClose, onSaved }: ClosuresManagerProps) {
  const push = useToastStore((s) => s.push);
  const [closures, setClosures] = useState<Closure[]>([]);
  const [holidays, setHolidays] = useState<Record<string, boolean>>({});
  const [editing, setEditing] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [oneOff, setOneOff] = useState<OneOffDraft>({
    from: today(),
    to: today(),
    allDay: true,
    startTime: "08:00",
    endTime: "12:00",
    label: "",
  });
  const [weekly, setWeekly] = useState<WeeklyDraft>({
    weekdays: [],
    startTime: "12:00",
    endTime: "23:59",
    from: today(),
    to: "",
    label: "",
  });

  // À chaque ouverture, repartir des réglages en vigueur.
  useEffect(() => {
    if (!open) return;
    setClosures(rules?.closures ?? []);
    setHolidays(rules?.holidays ?? {});
    setEditing(null);
  }, [open, rules]);

  if (!open) return null;

  const now = today();
  const upcoming = closures
    .filter((c) => c.to === null || c.to === undefined || c.to >= now)
    .sort((a, b) => a.from.localeCompare(b.from));
  const past = closures.length - upcoming.length;
  const year = new Date().getFullYear();
  const feries = [year, year + 1]
    .flatMap((y) => holidaysForYear(y, holidays))
    .filter((h) => h.date >= now);

  const patch = (id: string, changes: Partial<Closure>) =>
    setClosures((list) => list.map((c) => (c.id === id ? { ...c, ...changes } : c)));
  const remove = (id: string) => setClosures((list) => list.filter((c) => c.id !== id));

  const addOneOff = () => {
    if (!oneOff.from) return;
    const to = oneOff.to && oneOff.to >= oneOff.from ? oneOff.to : oneOff.from;
    if (!oneOff.allDay && oneOff.startTime >= oneOff.endTime) {
      push("danger", "Heures illisibles", "La fin doit suivre le début.");
      return;
    }
    setClosures((list) => [
      ...list,
      {
        id: newId(),
        from: oneOff.from,
        to,
        weekdays: null,
        ...(oneOff.allDay ? {} : { startTime: oneOff.startTime, endTime: oneOff.endTime }),
        label: oneOff.label.trim() || "Fermeture",
      },
    ]);
    setOneOff((d) => ({ ...d, label: "" }));
  };
  const addWeekly = () => {
    if (!weekly.weekdays.length) {
      push("danger", "Aucun jour choisi", "Cochez au moins un jour de la semaine.");
      return;
    }
    if (weekly.startTime >= weekly.endTime) {
      push("danger", "Heures illisibles", "La fin doit suivre le début.");
      return;
    }
    setClosures((list) => [
      ...list,
      {
        id: newId(),
        from: weekly.from || now,
        to: weekly.to || null,
        weekdays: weekly.weekdays,
        startTime: weekly.startTime,
        endTime: weekly.endTime,
        label: weekly.label.trim() || "Plage bloquée",
      },
    ]);
    setWeekly((d) => ({ ...d, weekdays: [], label: "" }));
  };

  const save = async () => {
    if (busy) return;
    const bad = closures.find((c) => c.to && c.to < c.from);
    if (bad) {
      push(
        "danger",
        "Dates illisibles",
        `${bad.label || "Une fermeture"} finit avant de commencer.`,
      );
      return;
    }
    setBusy(true);
    try {
      const saved = await updateAgendaRules({ closures, holidays });
      onSaved(saved);
      push(
        "success",
        "Agenda mis à jour",
        "Le site et Léo ne proposent plus ces moments ; au CRM, planifier dessus demande une dérogation.",
      );
      onClose();
    } catch {
      push("danger", "Enregistrement impossible", "Vérifiez les dates et les heures (HH:MM).");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      title="Fermetures et jours fériés"
      size="lg"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button variant="primary" disabled={busy} onClick={() => void save()}>
            Enregistrer
          </Button>
        </>
      }
    >
      <div className="closures">
        <p className="closures__intro">
          Tempête, déneigement, inventaire, plage réservée chaque semaine : ce qui est fermé ici
          disparaît des disponibilités du site et de Léo. Au CRM, un rendez-vous peut quand même s’y
          placer, avec une dérogation.
        </p>

        <section className="closures__section">
          <h3 className="closures__title">Fermer une journée ou une période</h3>
          <div className="closures__form">
            <Field label="Du">
              <Input
                type="date"
                value={oneOff.from}
                onChange={(e) =>
                  setOneOff((d) => ({
                    ...d,
                    from: e.target.value,
                    to: d.to < e.target.value ? e.target.value : d.to,
                  }))
                }
              />
            </Field>
            <Field label="Au">
              <Input
                type="date"
                value={oneOff.to}
                min={oneOff.from}
                onChange={(e) => setOneOff((d) => ({ ...d, to: e.target.value }))}
              />
            </Field>
            <Field label="Motif (interne)">
              <Input
                value={oneOff.label}
                maxLength={120}
                placeholder="ex : Tempête de neige, déneigement"
                onChange={(e) => setOneOff((d) => ({ ...d, label: e.target.value }))}
              />
            </Field>
            <div className="closures__form-row">
              <Toggle
                checked={oneOff.allDay}
                onChange={(v) => setOneOff((d) => ({ ...d, allDay: v }))}
                label={oneOff.allDay ? "Toute la journée" : "Une plage d’heures"}
              />
              {!oneOff.allDay ? (
                <>
                  <Input
                    type="time"
                    aria-label="Début"
                    value={oneOff.startTime}
                    onChange={(e) => setOneOff((d) => ({ ...d, startTime: e.target.value }))}
                  />
                  <Input
                    type="time"
                    aria-label="Fin"
                    value={oneOff.endTime}
                    onChange={(e) => setOneOff((d) => ({ ...d, endTime: e.target.value }))}
                  />
                </>
              ) : null}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                icon={<Icon name="plus" size={14} />}
                onClick={addOneOff}
              >
                Ajouter
              </Button>
            </div>
          </div>
        </section>

        <section className="closures__section">
          <h3 className="closures__title">Bloquer une plage chaque semaine</h3>
          <div className="closures__form">
            <div className="closures__span">
              <Field label="Jours">
                <WeekdayChips
                  value={weekly.weekdays}
                  onChange={(weekdays) => setWeekly((d) => ({ ...d, weekdays }))}
                />
              </Field>
            </div>
            <Field label="De">
              <Input
                type="time"
                value={weekly.startTime}
                onChange={(e) => setWeekly((d) => ({ ...d, startTime: e.target.value }))}
              />
            </Field>
            <Field label="À" hint="23:59 = jusqu’à la fin de la journée.">
              <Input
                type="time"
                value={weekly.endTime}
                onChange={(e) => setWeekly((d) => ({ ...d, endTime: e.target.value }))}
              />
            </Field>
            <Field label="À partir du">
              <Input
                type="date"
                value={weekly.from}
                onChange={(e) => setWeekly((d) => ({ ...d, from: e.target.value }))}
              />
            </Field>
            <Field label="Jusqu’au" hint="Vide : sans fin.">
              <Input
                type="date"
                value={weekly.to}
                min={weekly.from}
                onChange={(e) => setWeekly((d) => ({ ...d, to: e.target.value }))}
              />
            </Field>
            <Field label="Motif (interne)">
              <Input
                value={weekly.label}
                maxLength={120}
                placeholder="ex : Préparation des concessionnaires"
                onChange={(e) => setWeekly((d) => ({ ...d, label: e.target.value }))}
              />
            </Field>
            <div className="closures__form-row">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                icon={<Icon name="plus" size={14} />}
                onClick={addWeekly}
              >
                Ajouter la plage
              </Button>
            </div>
          </div>
        </section>

        <section className="closures__section">
          <h3 className="closures__title">
            Fermetures à venir
            {past > 0 ? (
              <span className="closures__muted">
                {" "}
                · {past} passée{past > 1 ? "s" : ""} conservée{past > 1 ? "s" : ""}
              </span>
            ) : null}
          </h3>
          {upcoming.length === 0 ? (
            <p className="closures__muted">Aucune fermeture prévue.</p>
          ) : (
            <ul className="closures__list">
              {upcoming.map((c) => (
                <li key={c.id} className={`closure-row${editing === c.id ? " is-editing" : ""}`}>
                  <div className="closure-row__head">
                    <span className="closure-row__text">
                      <strong>{c.label || "Fermeture"}</strong>
                      <span className="closures__muted"> · {describeClosure(c)}</span>
                    </span>
                    <span className="closure-row__actions">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        icon={<Icon name="edit" size={14} />}
                        onClick={() => setEditing(editing === c.id ? null : c.id)}
                      >
                        {editing === c.id ? "Fermer" : "Modifier"}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost-danger"
                        size="sm"
                        icon={<Icon name="trash" size={14} />}
                        onClick={() => remove(c.id)}
                      >
                        Retirer
                      </Button>
                    </span>
                  </div>
                  {editing === c.id ? (
                    <div className="closures__form">
                      <Field label="Du">
                        <Input
                          type="date"
                          value={c.from}
                          onChange={(e) => patch(c.id, { from: e.target.value })}
                        />
                      </Field>
                      <Field label="Au" hint="Vide : sans fin.">
                        <Input
                          type="date"
                          value={c.to ?? ""}
                          onChange={(e) => patch(c.id, { to: e.target.value || null })}
                        />
                      </Field>
                      <Field label="De" hint="Vide : toute la journée.">
                        <Input
                          type="time"
                          value={c.startTime ?? ""}
                          onChange={(e) => patch(c.id, { startTime: e.target.value || null })}
                        />
                      </Field>
                      <Field label="À">
                        <Input
                          type="time"
                          value={c.endTime ?? ""}
                          onChange={(e) => patch(c.id, { endTime: e.target.value || null })}
                        />
                      </Field>
                      <div className="closures__span">
                        <Field label="Jours" hint="Aucun : tous les jours de la période.">
                          <WeekdayChips
                            value={c.weekdays ?? []}
                            onChange={(weekdays) =>
                              patch(c.id, { weekdays: weekdays.length ? weekdays : null })
                            }
                          />
                        </Field>
                      </div>
                      <Field label="Motif (interne)">
                        <Input
                          value={c.label}
                          maxLength={120}
                          onChange={(e) => patch(c.id, { label: e.target.value })}
                        />
                      </Field>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="closures__section">
          <h3 className="closures__title">Jours fériés</h3>
          <p className="closures__muted">
            Calendrier du Québec, prérempli chaque année. Coché : fermé. Décochez un férié où
            l’atelier travaille.
          </p>
          <ul className="closures__holidays">
            {feries.map((h) => (
              <li key={`${h.key}-${h.date}`}>
                <Toggle
                  checked={h.closed}
                  onChange={(v) => setHolidays((prev) => ({ ...prev, [h.key]: v }))}
                  label={h.label}
                  hint={`${fullDate(h.date)} ${h.date.slice(0, 4)} · ${h.closed ? "fermé" : "ouvert"}`}
                />
              </li>
            ))}
          </ul>
        </section>
      </div>
    </Modal>
  );
}
